(function () {
  'use strict';
  if (customElements.get('stellar-bundles') || !window.StellarBundleCore) return;
  const core = window.StellarBundleCore;
  let coordinator;
  customElements.define('stellar-bundles', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      if (this.dataset.nativeSource === 'article' && !this.liveReady) { this.loadLiveNative(); return; }
      this.abort = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-bundle-config]').textContent);
      this.text = this.config.text;
      this.urls = core.routes(this.dataset.root);
      coordinator ||= new core.CartCoordinator(window.StellarCartSafety ? window.StellarCartSafety.rawFetch : window.fetch.bind(window), this.dataset.root, () => crypto.randomUUID());
      this.forms = [...this.querySelectorAll('[data-bundle-form]')];
      for (const form of this.forms) {
        const native = form.querySelector('[data-native-bundle-config]');
        form.nativeBundle = native ? JSON.parse(native.textContent) : null;
      }
      for (const row of this.querySelectorAll('[data-bundle-row]')) {
        row.bundleData = JSON.parse(row.querySelector('[data-row-config]').textContent);
        const component = row.closest('[data-bundle-form]').nativeBundle?.components.find(item => String(item.id) === row.querySelector('[data-variant]').value);
        row.bundleUnitQuantity = Number(component?.quantity || row.querySelector('[data-quantity]').defaultValue || 1);
        if (!Number.isSafeInteger(row.bundleUnitQuantity) || row.bundleUnitQuantity < 1) row.bundleUnitQuantity = 1;
        const included = row.querySelector('[data-bundle-included]');
        if (included) { included.textContent = included.dataset.labelTemplate.replace('__count__', String(row.bundleUnitQuantity)); included.hidden = false; }
        const include = row.querySelector('[data-include]');
        include.disabled = row.dataset.invalid === 'true';
        if (include.disabled) include.checked = false;
        this.bindSource(row);
        this.updateRow(row);
      }
      for (const form of this.forms) this.updateBundleQuantity(form);
      this.addEventListener('change', event => {
        if (event.target.matches('[data-bundle-quantity]')) { this.updateBundleQuantity(event.target.closest('[data-bundle-form]')); this.updateTotals(); return; }
        const row = event.target.closest('[data-bundle-row]');
        if (row) {
          const quantity = row.querySelector('[data-quantity]');
          if (event.target.matches('[data-variant-choice]')) row.querySelector('[data-variant]').value = event.target.value;
          if (event.target.matches('[data-include]')) {
            quantity.value = event.target.checked ? String(row.lastQuantity || this.firstQuantity(row)) : '0';
          }
          this.updateRow(row);
        }
        this.updateTotals();
      }, { signal: this.abort.signal });
      this.addEventListener('input', event => {
        if (event.target.matches('[data-bundle-quantity]')) { this.updateBundleQuantity(event.target.closest('[data-bundle-form]')); this.updateTotals(); return; }
        if (event.target.matches('[data-quantity]')) { this.updateRow(event.target.closest('[data-bundle-row]')); this.updateTotals(); }
      }, { signal: this.abort.signal });
      this.addEventListener('click', event => {
        const bundleStep = event.target.closest('[data-bundle-quantity-step]');
        if (bundleStep) {
          if (bundleStep.disabled) return;
          const form = bundleStep.closest('[data-bundle-form]'), input = form.querySelector('[data-bundle-quantity]');
          if (bundleStep.dataset.bundleQuantityStep === 'plus') input.stepUp(); else input.stepDown();
          this.updateBundleQuantity(form); this.updateTotals(); return;
        }
        const review = event.target.closest('[data-review-options]');
        if (review) {
          const form = review.closest('[data-bundle-form]');
          const row = this.selectedRows(form).find(item => item.querySelector('[data-inherit]')?.checked && item.sourceForm);
          if (row) this.revealSourceOptions(row);
          return;
        }
        const button = event.target.closest('[data-quantity-step]');
        if (!button || button.disabled) return;
        const row = button.closest('[data-bundle-row]'), quantity = row.querySelector('[data-quantity]');
        const variant = this.rowVariant(row), step = Number(variant?.rule.increment || 1), first = this.firstQuantity(row);
        const current = Number(quantity.value) || 0;
        let next = button.dataset.quantityStep === 'plus' ? (current < first ? first : (Math.floor(current / step) + 1) * step) : (Math.ceil(current / step) - 1) * step;
        if (next < first) next = 0;
        if (next > Math.min(Number(variant?.rule.max || 999), 999)) return;
        quantity.value = String(next); this.updateRow(row); this.updateTotals();
      }, { signal: this.abort.signal });
      this.addEventListener('submit', event => this.submit(event), { signal: this.abort.signal });
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.restoreCompletedVisit();
      }, { signal: this.abort.signal });
      this.addEventListener('load', () => this.positionSeparators(), { capture: true, signal: this.abort.signal });
      if (typeof ResizeObserver !== 'undefined') {
        this.layoutObserver = new ResizeObserver(() => this.positionSeparators());
        for (const grid of this.querySelectorAll('.stellar-bundle-grid')) this.layoutObserver.observe(grid);
      }
      this.updateTotals();
      this.positionSeparators();
    }
    async restoreCompletedVisit() {
      if (!this.finished || !coordinator?.completed || coordinator.uncertain || coordinator.busy || this.restoringVisit) return;
      this.restoringVisit = true;
      try {
        const cart = await coordinator.readCart();
        if (!this.isConnected || cart.currency !== this.config.currency) return;
        coordinator.completed = false;
        this.finished = false;
        this.busy = false;
        for (const form of this.forms) {
          form.removeAttribute('aria-busy');
          const status = form.querySelector('[data-status]');
          if (status) status.textContent = '';
        }
        this.updateTotals();
        this.dispatchEvent(new CustomEvent('stellar:bundle-cart-reconciled', { bubbles: true, detail: { cart, phase: 'restored' } }));
      } catch (_) {
        for (const form of this.forms) {
          const status = form.querySelector('[data-status]');
          if (status) status.textContent = this.text.review;
        }
      } finally {
        this.restoringVisit = false;
      }
    }
    async loadLiveNative() {
      if (this.liveLoading) return;
      const section = this.closest('.stellar-bundle-section');
      const fallback = section.querySelector('[data-native-fallback]');
      const retry = fallback?.querySelector('[data-native-retry]');
      const status = fallback?.querySelector('[data-native-load-status]');
      const text = JSON.parse(this.querySelector('[data-bundle-config]').textContent).text;
      section.hidden = false;
      if (fallback) fallback.hidden = false;
      if (retry) retry.onclick = () => this.loadLiveNative();
      if (!window.StellarLessonBundle) {
        if (retry) retry.hidden = false;
        if (!this.liveAwaiting) {
          this.liveAwaiting = true;
          window.addEventListener('stellar:lesson-bundle-ready', () => {
            this.liveAwaiting = false;
            if (this.isConnected) this.connectedCallback();
          }, { once: true });
        }
        return;
      }
      this.liveLoading = true;
      this.liveAbort = new AbortController();
      if (retry) { retry.hidden = false; retry.disabled = true; }
      if (status) status.textContent = text.loading_options;
      this.dataset.nativeLoadState = 'loading';
      try {
        await window.StellarLessonBundle.hydrate(this, this.liveAbort.signal, native => {
          if (!fallback || !this.isConnected) return;
          const format = amount => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: native.currency, currencyDisplay: 'narrowSymbol', ...(Number(amount) % 100 === 0 ? { minimumFractionDigits: 0 } : {}) }).format(amount / 100);
          const regular = native.components.reduce((sum, component) => sum + component.price * component.quantity, 0);
          const saving = Math.max(0, regular - native.price);
          fallback.querySelector('[data-native-price]').textContent = format(native.price);
          fallback.querySelector('[data-native-original-wrap]').hidden = !saving;
          fallback.querySelector('[data-native-original]').textContent = saving ? format(regular) : '';
          fallback.querySelector('[data-native-saving-wrap]').hidden = !saving;
          fallback.querySelector('[data-native-saving]').textContent = saving ? format(saving) : '';
          const tools = fallback.querySelector('[data-native-tools]');
          tools.replaceChildren();
          for (const component of native.components) {
            const link = document.createElement('a');
            link.href = core.routes(this.dataset.root).root + 'products/' + encodeURIComponent(component.handle) + '?variant=' + component.id;
            if (component.image?.url && /^https:\/\//.test(component.image.url)) {
              const image = document.createElement('img');
              image.src = component.image.url; image.alt = component.image.altText || component.title;
              image.loading = 'lazy'; image.width = 180; image.height = 180; link.append(image);
            }
            const title = document.createElement('span'); title.textContent = component.title || component.handle;
            link.append(title); tools.append(link);
          }
          tools.hidden = !native.components.length;
        });
        if (!this.isConnected) return;
        this.liveReady = true;
        this.connectedCallback();
        for (const form of this.querySelectorAll('[data-bundle-form]')) {
          form.hidden = false;
          form.querySelector('.stellar-bundle-grid').hidden = false;
        }
        if (fallback) fallback.hidden = true;
        this.dataset.nativeLoadState = 'ready';
        delete this.dataset.nativeLoadError;
        this.positionSeparators();
      } catch (error) {
        if (!this.isConnected) return;
        this.liveReady = false;
        this.abort?.abort(); this.abort = null; this.layoutObserver?.disconnect();
        for (const form of this.querySelectorAll('[data-bundle-form]')) {
          const choice = form.querySelector('[data-native-choice]');
          form.hidden = !choice;
          if (choice) {
            choice.disabled = false;
            form.querySelector('.stellar-bundle-grid').hidden = true;
            for (const button of form.querySelectorAll('[type="submit"]')) button.disabled = true;
            form.querySelector('[data-status]').textContent = text.options_unavailable;
          }
        }
        if (fallback) fallback.hidden = false;
        if (status) status.textContent = text.options_unavailable;
        this.dataset.nativeLoadState = 'fallback'; this.dataset.nativeLoadError = error.message || 'unavailable';
        this.dispatchEvent(new CustomEvent('stellar:bundle-load-error', { bubbles: true, detail: { code: this.dataset.nativeLoadError } }));
      } finally {
        this.liveLoading = false;
        if (this.liveReady && this.forms) this.updateTotals();
        if (retry) retry.disabled = false;
      }
    }
    positionSeparators() {
      for (const grid of this.querySelectorAll('.stellar-bundle-grid')) {
        let previous;
        for (const row of grid.querySelectorAll('[data-bundle-row]')) {
          const media = row.querySelector('.stellar-bundle-media'), link = row.querySelector('[data-product-link]'), plus = row.querySelector('.stellar-bundle-plus');
          if (!media || !link || !plus) continue;
          const image = link.getBoundingClientRect(), origin = media.getBoundingClientRect();
          plus.hidden = true;
          if (previous && image.width > 0 && image.height > 0) {
            const sameRow = Math.abs(image.top - previous.top) < 2;
            const sameColumn = Math.abs(image.left - previous.left) < 2;
            if (sameRow || sameColumn) {
              const x = sameRow ? (previous.right + image.left) / 2 : image.left + image.width / 2;
              const y = sameRow ? image.top + image.height / 2 : (previous.bottom + image.top) / 2;
              plus.style.left = (x - origin.left) + 'px';
              plus.style.top = (y - origin.top) + 'px';
              plus.hidden = false;
            }
          }
          previous = image;
        }
      }
    }
    revealSourceOptions(row) {
      const source = row.sourceForm;
      if (!source) return;
      for (const details of source.querySelectorAll('details.stellar-purchase-options')) details.open = true;
      source.scrollIntoView({ block: 'center', behavior: 'auto' });
      const field = source.querySelector('[aria-invalid="true"], :invalid') || source.querySelector('input:not([type="hidden"]):not([disabled]), button:not([disabled])');
      field?.focus({ preventScroll: true });
    }
    bindSource(row) {
      const inherit = row.querySelector('[data-inherit]');
      if (!inherit) return;
      const ids = new Set(row.bundleData.variants.map(v => String(v.id)));
      const forms = [...document.querySelectorAll('[id^="MainProduct-"] product-form form[id^="product-form-"]')].filter(form => ids.has(String(new FormData(form).get('id'))));
      row.sourceForm = forms.length === 1 ? forms[0] : null;
      if (row.sourceForm) for (const type of ['change', 'input', 'stellar:purchase-state']) row.sourceForm.addEventListener(type, () => {
        this.updateRow(row); this.updateTotals();
      }, { signal: this.abort.signal });
    }
    rowVariant(row) { return row.bundleData.variants.find(v => String(v.id) === row.querySelector('[data-variant]').value); }
    firstQuantity(row) {
      const rule = this.rowVariant(row)?.rule, step = Number(rule?.increment || 1);
      return Math.ceil(Number(rule?.min || 1) / step) * step;
    }
    updateBundleQuantity(form) {
      const input = form.querySelector('[data-bundle-quantity]');
      if (!input) return;
      const rows = [...form.querySelectorAll('[data-bundle-row]')];
      const parentRule = form.nativeBundle?.rule;
      const increment = Number(parentRule?.increment || 1);
      const min = Math.ceil(Number(parentRule?.min || 1) / increment) * increment;
      let max = Math.min(Number(parentRule?.max || 999), 999);
      for (const row of rows) max = Math.min(max, Math.floor(Math.min(Number(this.rowVariant(row)?.rule.max || 999), 999) / row.bundleUnitQuantity));
      input.min = String(min); input.max = String(max); input.step = String(increment);
      const amount = Number(input.value);
      const valid = input.value !== '' && Number.isSafeInteger(amount) && amount >= min && amount <= max && amount % increment === 0;
      input.setCustomValidity(valid ? '' : this.text.quantity);
      form.dataset.fixedBundle = 'true';
      for (const row of rows) {
        row.querySelector('[data-quantity]').value = String(row.bundleUnitQuantity * (valid ? amount : min));
        this.updateRow(row);
      }
      form.querySelector('[data-bundle-quantity-step="minus"]').disabled = this.busy || this.finished || !valid || amount <= min;
      form.querySelector('[data-bundle-quantity-step="plus"]').disabled = this.busy || this.finished || !valid || amount + increment > max;
      input.disabled = this.busy || this.finished;
    }
    updateRow(row) {
      const include = row.querySelector('[data-include]'), select = row.querySelector('[data-variant]');
      const quantity = row.querySelector('[data-quantity]'), inherit = row.querySelector('[data-inherit]');
      if (inherit?.checked && row.sourceForm) select.value = String(new FormData(row.sourceForm).get('id'));
      const variant = this.rowVariant(row), invalid = row.dataset.invalid === 'true';
      const unavailable = !variant?.available, max = Math.min(Number(variant?.rule.max || 999), 999);
      if (invalid || unavailable) quantity.value = '0';
      const amount = Number(quantity.value), included = Number.isSafeInteger(amount) && amount > 0;
      include.checked = included; include.disabled = invalid || unavailable;
      if (included) row.lastQuantity = amount;
      quantity.disabled = invalid || unavailable; quantity.min = 0; quantity.step = 1; quantity.max = max;
      const valid = quantity.value !== '' && Number.isSafeInteger(amount) && amount >= 0 && amount <= max && (!amount || amount >= this.firstQuantity(row) && amount % Number(variant?.rule.increment || 1) === 0);
      quantity.setCustomValidity(valid ? '' : this.text.quantity);
      for (const choice of row.querySelectorAll('[data-variant-choice]')) {
        choice.checked = choice.value === select.value;
        choice.disabled = invalid || !!inherit?.checked || !row.bundleData.variants.find(v => String(v.id) === choice.value)?.available;
      }
      row.querySelector('[data-quantity-step="minus"]').disabled = quantity.disabled || amount <= 0;
      row.querySelector('[data-quantity-step="plus"]').disabled = quantity.disabled || (amount < this.firstQuantity(row) ? this.firstQuantity(row) : (Math.floor(amount / Number(variant?.rule.increment || 1)) + 1) * Number(variant?.rule.increment || 1)) > max;
      if (variant) row.querySelector('[data-row-price]').textContent = this.money(variant.price);
      const image = row.querySelector('[data-product-link] img');
      if (image && variant && variant.image) {
        image.removeAttribute('srcset');
        image.src = variant.image;
        image.alt = variant.imageAlt || image.alt;
      }
      if (variant) for (const productLink of row.querySelectorAll('[data-product-link], [data-product-name-link]')) productLink.href = this.urls.root + 'products/' + encodeURIComponent(row.bundleData.handle) + '?variant=' + encodeURIComponent(variant.id);
      row.querySelector('[data-row-state]').textContent = unavailable ? this.text.unavailable : included && inherit?.checked && !row.sourceForm ? this.text.options_missing : '';
    }
    money(amount) { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.config.currency, currencyDisplay: 'narrowSymbol', ...(Number(amount) % 100 === 0 ? { minimumFractionDigits: 0 } : {}) }).format(amount / 100); }
    selectedRows(form) { return [...form.querySelectorAll('[data-bundle-row]')].filter(row => row.querySelector('[data-include]').checked && Number(row.querySelector('[data-quantity]').value) > 0); }
    nativeChoice(form, rows) {
      return core.nativeSelection(rows.map(row => {
        const variant = this.rowVariant(row);
        return { id: variant?.id, price: variant?.price, available: variant?.available, quantity: Number(row.querySelector('[data-quantity]').value), hasOptions: !!row.querySelector('[data-inherit]')?.checked };
      }), form.nativeBundle);
    }
    verifiedRrpSavings(form, rows, native) {
      if (!native || this.dataset.nativeSource !== 'article' || form.nativeBundle?.country !== 'AU' || this.config.currency !== 'AUD') return null;
      let regular = 0;
      for (const row of rows) {
        const variant = this.rowVariant(row), quantity = Number(row.querySelector('[data-quantity]').value);
        if (variant?.rrpCurrency !== 'AUD' || !Number.isSafeInteger(variant.rrp) || variant.rrp <= 0 || !Number.isSafeInteger(quantity) || quantity < 1) return null;
        regular += variant.rrp * quantity;
      }
      if (!Number.isSafeInteger(regular) || regular <= 0 || !Number.isSafeInteger(native.total) || native.total < 0 || native.total >= regular) return null;
      const saving = regular - native.total, percent = Math.round(saving * 100 / regular);
      return percent > 0 && percent <= 100 ? { regular, saving, percent } : null;
    }
    updateTotals() {
      for (const form of this.forms) {
        this.updateBundleQuantity(form);
        const rows = this.selectedRows(form);
        const total = rows.reduce((sum, row) => {
          const variant = row.bundleData.variants.find(v => String(v.id) === row.querySelector('[data-variant]').value);
          return sum + Number(variant?.price || 0) * Number(row.querySelector('[data-quantity]').value || 0);
        }, 0);
        const native = this.nativeChoice(form, rows);
        form.querySelector('[data-total]').textContent = this.money(native ? native.total : total);
        const rrp = this.verifiedRrpSavings(form, rows, native);
        const selector = form.querySelector('[data-native-choice]');
        if (selector) selector.disabled = this.busy || this.finished || this.liveLoading;
        const selectionStatus = form.querySelector('[data-status]');
        if (form.nativeBundle?.migration && !this.busy && !this.finished && !this.liveLoading && selectionStatus.textContent === this.text.selection) selectionStatus.textContent = ''; 
        const lesson = this.dataset.nativeSource === 'article';
        const saving = lesson ? rrp?.saving : native?.saving;
        const original = form.querySelector('[data-component-total-wrap]');
        if (original) { original.hidden = !saving; original.querySelector('[data-component-total]').textContent = saving ? this.money(lesson ? rrp.regular : total) : ''; }
        const savings = form.querySelector('[data-bundle-savings]');
        if (savings) { savings.hidden = !saving; savings.querySelector('[data-savings-amount]').textContent = saving ? this.money(saving) + (rrp ? ' (' + rrp.percent + '%)' : '') : ''; }
        for (const button of form.querySelectorAll('[type="submit"]')) button.disabled = this.busy || this.finished || !rows.length || [...form.querySelectorAll('[data-quantity], [data-bundle-quantity]')].some(input => !input.disabled && !input.validity.valid);
      }
    }
    sourceOptions(row, variantId, quantity) {
      if (!row.querySelector('[data-inherit]')?.checked) return { properties: {}, addons: [] };
      const form = row.sourceForm;
      if (!form || !form.reportValidity()) throw new Error('options_missing');
      const source = new FormData(form);
      if (String(source.get('id')) !== variantId) throw new Error('selection');
      const personal = form.querySelector('.product-personalisation-wrap input[type="checkbox"]');
      const wrap = form.querySelector('.product-gift-wrap .product-gift-checkbox');
      const personalSelected = personal?.checked && !personal.disabled;
      const wrapSelected = wrap?.checked && !wrap.disabled;
      const properties = Object.create(null), addons = [];
      for (const [name, value] of source.entries()) {
        const match = /^properties\[(.*)\]$/.exec(name);
        if (!match || match[1] === 'Personalisation' && personal || match[1] === 'Gift Wrapping' && wrap || match[1] === 'Trade up' && value === 'false') continue;
        properties[match[1]] = value;
      }
      if (personalSelected) {
        const value = String(source.get('properties[Personalisation]') || '').trim();
        if (!value || !core.validId(personal.value) || !window.StellarEngraving) throw new Error('options_missing');
        if (window.StellarEngraving.reflect(form, true)) throw new Error('options_missing');
        properties.Personalisation = value;
        addons.push({ id: personal.value, quantity, properties: {} });
      }
      if (wrapSelected) {
        const option = [...form.querySelectorAll('#variant-options .variant-option')].find(option => option.dataset.variantId === wrap.value);
        const name = (option?.dataset.variantName || wrap.dataset.variantName || '').trim();
        if (!core.validId(wrap.value) || !name) throw new Error('options_missing');
        properties['Gift Wrapping'] = name;
        addons.push({ id: wrap.value, quantity, properties: {} });
      }
      return { properties, addons, selling_plan: source.get('selling_plan') || undefined };
    }
    async prepare(form, cart) {
      if (cart.currency !== this.config.currency) throw new Error('currency');
      if (form.querySelector('[data-live-native-bundle]')) await window.StellarLessonBundle.verify(form.nativeBundle);
      const rows = this.selectedRows(form);
      if (!rows.length) throw new Error('selection');
      const products = new Map(), items = [], requested = new Map();
      for (const row of rows) {
        const config = row.bundleData;
        if (row.dataset.invalid === 'true') throw new Error('selection');
        this.updateRow(row);
        const id = row.querySelector('[data-variant]').value;
        const variant = config.variants.find(v => String(v.id) === id);
        const quantity = Number(row.querySelector('[data-quantity]').value);
        if (!products.has(config.handle)) {
          const response = await fetch(this.urls.root + 'products/' + encodeURIComponent(config.handle) + '.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('unavailable');
          products.set(config.handle, await response.json());
        }
        const fresh = products.get(config.handle), freshVariant = fresh.variants?.find(v => String(v.id) === id);
        const existing = cart.items.filter(item => String(item.variant_id || item.id) === id).reduce((sum, item) => sum + item.quantity, 0);
        const alreadyRequested = requested.get(id) || 0;
        core.checkSelection(freshVariant, quantity, variant?.rule, existing + alreadyRequested);
        if (fresh.requires_selling_plan || freshVariant.price !== variant.price) throw new Error('price_changed');
        requested.set(id, alreadyRequested + quantity);
        const options = this.sourceOptions(row, id, quantity);
        items.push({ id, quantity, properties: options.properties, selling_plan: options.selling_plan }, ...options.addons);
      }
      const native = this.nativeChoice(form, rows);
      if (native) {
        if (items.length !== rows.length || items.some(item => Object.keys(item.properties).length || item.selling_plan)) throw new Error('options_missing');
        const config = form.nativeBundle;
        const response = await fetch(this.urls.root + 'products/' + encodeURIComponent(config.handle) + '.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('unavailable');
        const fresh = await response.json(), variant = fresh.variants?.find(item => String(item.id) === native.id);
        const existing = cart.items.filter(item => String(item.variant_id || item.id) === native.id).reduce((sum, item) => sum + item.quantity, 0);
        core.checkSelection(variant, native.quantity, config.rule, existing);
        if (String(fresh.id) !== config.productId || fresh.requires_selling_plan || variant.price !== config.price) throw new Error('price_changed');
        return [{ id: native.id, quantity: native.quantity, properties: {} }, ...native.extras.map(item => ({ id: item.id, quantity: item.quantity, properties: {} }))];
      }
      return items;
    }
    async openCartDrawer(trigger) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') throw new Error('drawer_unavailable');
      const sectionIds = drawer.getSectionsToRender().map(section => section.id);
      const url = new URL(this.urls.root, window.location.origin);
      url.searchParams.set('sections', sectionIds.join(','));
      const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('drawer_unavailable');
      const sections = await response.json();
      if (sectionIds.some(id => typeof sections[id] !== 'string' || !sections[id].trim())) throw new Error('drawer_unavailable');
      if (trigger) drawer.setActiveElement(trigger);
      drawer.classList.remove('is-empty');
      drawer.renderContents({ sections });
    }
    async submit(event) {
      const form = event.target.closest('[data-bundle-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault(); event.stopPropagation();
      if (this.busy || this.finished || !form.reportValidity() || !this.selectedRows(form).length) return;
      const checkout = event.submitter?.dataset.action === 'checkout';
      this.busy = true; this.updateTotals();
      const status = form.querySelector('[data-status]');
      if (this.dataset.linkedGiftsEnabled === 'true' && !window.StellarLinkedGifts) {
        this.busy = false; this.updateTotals(); status.textContent = this.text.review; status.focus(); return;
      }
      status.textContent = this.text.adding;
      form.setAttribute('aria-busy', 'true');
      const operation = async () => this.dataset.linkedGiftsEnabled === 'true'
        ? await window.StellarLinkedGifts.guardedSubmit(coordinator, async cart => {
            const items = await this.prepare(form, cart);
            const rows = this.selectedRows(form), native = this.nativeChoice(form, rows);
            if (!native) return { items, gifts: [] };
            const link = crypto.randomUUID();
            const gifts = window.StellarLinkedGifts.plan(
              { ...form.nativeBundle, quantity: native.quantity },
              rows.map(row => ({ id: this.rowVariant(row).id, physicalGifts: row.bundleData.physicalGifts })),
              link
            );
            if (gifts.length) items[0].properties._stellar_gift_link = link;
            return { items: [...items, ...gifts], gifts };
          }, form.dataset.offerId, true)
        : await coordinator.submit(cart => this.prepare(form, cart), form.dataset.offerId);
      const result = window.StellarCartSafety ? await window.StellarCartSafety.transaction(operation) : await operation();
      if (!this.isConnected) return;
      this.busy = false; form.removeAttribute('aria-busy');
      if (result.phase === 'success' && (this.dataset.linkedGiftsEnabled !== 'true' || result.checkoutAllowed === true)) {
        this.finished = true; this.updateTotals(); status.textContent = this.text.added;
        this.dispatchEvent(new CustomEvent('stellar:bundle-cart-reconciled', { bubbles: true, detail: { cart: result.cart, phase: result.phase } }));
        if (checkout) { window.location.assign(this.urls.checkout); return; }
        try {
          await this.openCartDrawer(event.submitter);
          coordinator.completed = false;
          this.finished = false;
          this.updateTotals();
        } catch (_) {
          // The bundle is already added: offer a cart link without submitting it again.
          form.querySelector('[data-review-cart]').hidden = false;
          status.textContent = this.text.added;
          status.focus();
        }
        return;
      }
      if (result.sent) this.finished = true;
      const key = result.phase === 'not_sent' ? result.error : 'review';
      status.textContent = this.text[key] || this.text.selection;
      const reviewOptions = form.querySelector('[data-review-options]');
      if (reviewOptions) reviewOptions.hidden = key !== 'options_missing' || !this.selectedRows(form).some(row => row.querySelector('[data-inherit]')?.checked && row.sourceForm);
      form.querySelector('[data-review-cart]').hidden = !result.sent;
      if (result.cart) this.dispatchEvent(new CustomEvent('stellar:bundle-cart-reconciled', { bubbles: true, detail: { cart: result.cart, phase: result.phase } }));
      this.updateTotals(); status.focus();
    }
    disconnectedCallback() { this.liveAbort?.abort(); this.abort?.abort(); this.layoutObserver?.disconnect(); this.abort = null; }
  });
})();



